import {forAnyObject} from './models/Scenario';
import AssistanceRequest from './models/AssistanceRequest';
import {CLOSED, OPEN} from './models/AlarmState';
import {V4_RECEIVER_STATUS} from './models/v4AlarmSituation';

// POST /v1/alarms takes only explicit uuids, so "All" is expanded here, within the owner's account.

const ownerOf = (entity) => entity?.get?.('v4OwnerId') || entity?.get?.('v4Data')?.owner_id || null;

const listOf = (entities) => (typeof entities?.toArray === 'function' ? entities.toArray() : entities || []);

const inScope = (entity, {ownerId, accountId}) => {
    const owner = ownerOf(entity);
    if (owner) return owner === ownerId;
    return !accountId || entity.get('accountId') === accountId;
};

const withEvery = (picked, entities, scope, keep = () => true) => {
    if (!Array.isArray(picked) || !picked.includes(forAnyObject)) return picked;
    const ids = listOf(entities)
        .filter((entity) => entity && keep(entity) && inScope(entity, scope))
        .map((entity) => entity.get('id'));
    return [...new Set([...picked.filter((v) => v !== forAnyObject), ...ids])];
};

const isAppDevice = (device) => !!(device.isApp || device.isCueblyMobile);

// Only apps take assistance requests (V2 app included, as a cuebly-mobile device).
export const hasAppDevice = (devices, scope = {}) =>
    listOf(devices).some((device) => device && isAppDevice(device) && inScope(device, scope));

export function withEveryRecipient(config, {devices, teams, geofences, ownerId, accountId} = {}) {
    const recipients = config?.recipients;
    if (!recipients) return config;
    const deviceScope = {ownerId, accountId};
    const scope = {ownerId};
    return {
        ...config,
        recipients: {
            ...recipients,
            devices: withEvery(recipients.devices, devices, deviceScope, isAppDevice),
            teams: withEvery(recipients.teams, teams, scope),
            geofences: withEvery(recipients.geofences, geofences, scope),
        },
    };
}

const RECIPIENT_KEYS = ['device_ids', 'team_ids', 'geofence_ids'];

// V4's all_* recipient flags are device-relative, so only explicit uuids are sent.
export function createAlarmRequestFrom(v4Config) {
    const recipients = {};
    RECIPIENT_KEYS.forEach((key) => {
        const ids = [...new Set((v4Config?.recipients?.[key] || []).filter(Boolean).map(String))];
        if (ids.length) recipients[key] = ids;
    });
    const body = {
        type: 'assistance_request',
        title: (v4Config?.name || '').trim(),
        message: (v4Config?.message || '').trim(),
        recipients,
    };
    if (v4Config?.language) body.language = v4Config.language;
    return body;
}

export const hasRecipients = (body) => RECIPIENT_KEYS.some((key) => body?.recipients?.[key]?.length > 0);

// V4 zero timestamps read `0001-01-01T…`.
const toUnix = (ts) => {
    if (!ts || String(ts).startsWith('0001-')) return 0;
    const ms = new Date(ts).getTime();
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : 0;
};

const text = (value) => (typeof value === 'string' ? value.trim() : '');

// The V3 page reads `acknowledged` > 0 as seen and `response_time` > 0 as answered. A receiver
// keeps only its latest state, so its update is the answer (or, while pending, the ack) time.
function toV3Response(receiver, sentAt) {
    const deviceId = receiver.device_id;
    const at = toUnix(receiver.updated_at) || toUnix(receiver.created_at) || sentAt;
    const base = {id: deviceId, device_id: deviceId, acknowledged: 0, response_time: 0, response: false};
    switch (Number(receiver.response?.status)) {
        case V4_RECEIVER_STATUS.PENDING:
            return {...base, acknowledged: at};
        case V4_RECEIVER_STATUS.ACCEPTED:
        case V4_RECEIVER_STATUS.REJECTED:
            return {
                ...base,
                acknowledged: toUnix(receiver.created_at) || at,
                response_time: at,
                response: Number(receiver.response.status) === V4_RECEIVER_STATUS.ACCEPTED,
            };
        case V4_RECEIVER_STATUS.FAILED:
            return {...base, failed: true};
        default:
            return base;
    }
}

// A V4 assistance request (an Alarm Record) in the V3 page's shape, so V3 users see it there.
export function toV3AssistanceRequest(alarm) {
    const v4Data = alarm.get('v4Data') || {};
    const meta = v4Data.metadata || {};
    const name = text(meta.name) || text(meta.title);
    const sentAt = alarm.get('date') || 0;
    const receivers = Array.isArray(v4Data.receivers) ? v4Data.receivers : [];
    return new AssistanceRequest({
        id: alarm.get('id'),
        name,
        message: text(meta.message),
        // One group per request, sorted by time like V3's "<time>_…" groups.
        groupName: `${sentAt}_${alarm.get('id')}`,
        time: sentAt,
        checked: alarm.get('checked') || 0,
        snapshot: {scenario: {name}},
        state: alarm.isOpen ? OPEN : CLOSED,
        deviceId: alarm.get('deviceId'),
        originName: alarm.get('deviceName') || '',
        scenarioId: 0,
        eventId: 0,
        responses: receivers
            .filter((r) => r && r.device_id && !toUnix(r.deleted_at))
            .map((r) => toV3Response(r, sentAt)),
        // The Record's notes, so a close's notes show before the closed list reloads.
        notes: typeof alarm.get('notes') === 'string' ? alarm.get('notes') : '',
        v4: true,
        v4Alarm: alarm,
    });
}
